import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RosterBuilder from '../components/RosterBuilder';
import ScoringRulesEditor from '../components/ScoringRulesEditor';
import RosterConfigEditor from '../components/RosterConfigEditor';
import AiButton from '../components/AiButton';
import StartSitPanel from '../components/StartSitPanel';
import MatchupPreviewBadge from '../components/MatchupPreviewBadge';
import MatchupPreviewPanel from '../components/MatchupPreviewPanel';
import TradeAnalysisPanel from '../components/TradeAnalysisPanel';
import {
  getLeague,
  updateLeagueRoster,
  updateLeagueScoringRules,
  updateLeagueRosterConfig,
  deleteLeague,
  getAggregatedRankings,
  getStartSitRecommendation,
  getMatchupPreview,
  analyzeTrade,
  getLeagueTeams,
  getLeagueTeamRoster,
  putLeagueTeamRoster,
  refreshOpponentRosters,
  populateRostersFromDraft,
  getOpponents,
  linkOpponentToLeagueSlot,
  unlinkOpponentFromLeagueSlot,
  createOpponent,
  getAiUsage,
} from '../services/api';
import OpponentPicker from '../components/OpponentPicker';
import { MATCHUP_PREVIEW_ENABLED } from '../config/featureFlags';
import { getAiErrorMessage } from '../utils/aiErrors';

// Known NFL season kickoff dates. Add one entry per year, ahead of need.
const SEASON_START_DATES = { 2025: '2025-09-04', 2026: '2026-09-10' };

// Resolves the in-progress season/week for "now", or the next known season
// during the off-season. Window is kickoff -> kickoff + 20 weeks (regular
// season + playoffs) — deliberately not re-clamped to 18 weeks, since a hard
// clamp is what caused this to get stuck reporting "Week 18" forever.
function getCurrentNflWeek() {
  const now = new Date();
  const msPerWeek = 7 * 24 * 60 * 60 * 1000;
  const years = Object.keys(SEASON_START_DATES).map(Number).sort((a, b) => a - b);
  for (const year of years) {
    const kickoff = new Date(`${SEASON_START_DATES[year]}T00:00:00Z`);
    const windowEnd = new Date(kickoff.getTime() + 20 * msPerWeek);
    if (now >= kickoff && now < windowEnd) {
      const week = Math.floor((now - kickoff) / msPerWeek) + 1;
      return { week, seasonYear: year, isOffSeason: false };
    }
  }
  const nextYear = years.find(y => new Date(`${SEASON_START_DATES[y]}T00:00:00Z`) > now);
  const fallbackYear = nextYear ?? (Math.max(...years) + 1);
  return { week: 1, seasonYear: fallbackYear, isOffSeason: true };
}

// The Start/Sit card is always shown, regardless of season — the "Ask the Doctor for My Lineup" button itself
// is disabled (not the whole card hidden) for a strictly past season (isPastSeasonLeague) and on
// Mondays, since most games are already locked/in-progress by then. Keeping the card visible in
// both cases lets users still see last week's context / know the feature exists rather than have
// it silently disappear.
function isMonday() {
  return new Date().getDay() === 1; // 0=Sun … 6=Sat
}

/**
 * In-season league view: shows roster, scoring rules, inline edit mode, and AI features.
 */
function LeagueView() {
  const { leagueId } = useParams();
  const { isLoggedIn, isLoading: authLoading, user } = useAuth();
  const isPro = user?.tier === 'pro';
  const navigate = useNavigate();
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editRoster, setEditRoster] = useState([]);
  const [scoringRules, setScoringRules] = useState({});
  const [rosterConfig, setRosterConfig] = useState({});
  const [rankings, setRankings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Delete mode
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // S1 — Start/Sit
  const [startSit, setStartSit] = useState(null);
  const [startSitLoading, setStartSitLoading] = useState(false);
  const [startSitError, setStartSitError] = useState(null);

  // S2 — Matchup Preview (keyed by player index)
  const [matchupData, setMatchupData] = useState({});   // { [playerIdx]: data | 'loading' }
  const [activeMatchupIdx, setActiveMatchupIdx] = useState(null);
  const [matchupError, setMatchupError] = useState(null);

  // Weekly AI usage remaining (D1/D3/S1/S3 share the same weekly counter)
  const [aiUsageRemaining, setAiUsageRemaining] = useState(20);

  // S3 — Trade Analysis
  const [givingPlayers, setGivingPlayers] = useState([]);
  const [receivingPlayers, setReceivingPlayers] = useState([]);
  const [receiveSearch, setReceiveSearch] = useState('');
  const [tradeResult, setTradeResult] = useState(null);
  const [tradeLoading, setTradeLoading] = useState(false);
  const [tradeError, setTradeError] = useState(null);

  // S3 Part 3 — "Trading with" opponent selection
  const [tradeWith, setTradeWith] = useState(''); // '' | `slot:${n}` | 'other'
  const [theirRoster, setTheirRoster] = useState([]); // [{ playerName, position }]
  const [theirRosterKnown, setTheirRosterKnown] = useState(false);
  const [theirRosterDirty, setTheirRosterDirty] = useState(false);
  const [theirSearch, setTheirSearch] = useState('');
  const [savingRoster, setSavingRoster] = useState(false);
  const [editingTheirRoster, setEditingTheirRoster] = useState(false);
  const [refreshingRosters, setRefreshingRosters] = useState(false);
  const [populatingRosters, setPopulatingRosters] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);

  const selectedSlotIndex = tradeWith.startsWith('slot:') ? Number(tradeWith.slice(5)) : null;
  const isAdHocOpponent = tradeWith === 'other';
  const showTheirRosterEditor = isAdHocOpponent || (selectedSlotIndex !== null && (!theirRosterKnown || editingTheirRoster));

  // Only reachable when there's no known opponent roster to pick from directly
  // (see the "You receive" checkbox-list branch in the render below).
  const receiveSearchResults = useMemo(() => {
    if (!receiveSearch.trim()) return [];
    const q = receiveSearch.toLowerCase();
    return rankings
      .filter(r =>
        !receivingPlayers.includes(r.playerName) &&
        (r.playerName.toLowerCase().includes(q) || (r.position || '').toLowerCase() === q)
      )
      .slice(0, 10);
  }, [receiveSearch, rankings, receivingPlayers]);

  const theirSearchResults = useMemo(() => {
    if (!theirSearch.trim()) return [];
    const q = theirSearch.toLowerCase();
    const have = new Set(theirRoster.map(p => p.playerName));
    return rankings
      .filter(r =>
        !have.has(r.playerName) &&
        (r.playerName.toLowerCase().includes(q) || (r.position || '').toLowerCase() === q)
      )
      .slice(0, 10);
  }, [theirSearch, rankings, theirRoster]);

  async function fetchSlotRoster(idx) {
    try {
      const r = await getLeagueTeamRoster(leagueId, idx);
      setTheirRoster(r.map(p => ({ playerName: p.playerName, position: p.position })));
    } catch {
      setTheirRoster([]);
    }
  }

  async function handleTradeWithChange(value) {
    setTradeWith(value);
    setTheirRoster([]);
    setTheirRosterDirty(false);
    setTheirSearch('');
    setEditingTheirRoster(false);
    setTradeResult(null);
    if (value.startsWith('slot:')) {
      const idx = Number(value.slice(5));
      const slot = leagueTeams.find(t => t.slotIndex === idx);
      if (slot?.rosterKnown) {
        setTheirRosterKnown(true);
        await fetchSlotRoster(idx);
      } else {
        setTheirRosterKnown(false);
      }
    } else {
      setTheirRosterKnown(false);
    }
  }

  function startEditingTheirRoster() {
    setEditingTheirRoster(true);
    setTheirRosterDirty(false);
  }

  async function cancelEditingTheirRoster() {
    setEditingTheirRoster(false);
    setTheirSearch('');
    setTheirRosterDirty(false);
    if (selectedSlotIndex !== null) {
      await fetchSlotRoster(selectedSlotIndex);
    }
  }

  function addTheirPlayer(name, position) {
    setTheirRoster(prev => (prev.some(p => p.playerName === name) ? prev : [...prev, { playerName: name, position }]));
    setTheirRosterDirty(true);
    setTheirSearch('');
  }

  function removeTheirPlayer(name) {
    setTheirRoster(prev => prev.filter(p => p.playerName !== name));
    setTheirRosterDirty(true);
  }

  async function handleSaveTheirRoster() {
    if (selectedSlotIndex === null) return;
    setSavingRoster(true);
    try {
      await putLeagueTeamRoster(leagueId, selectedSlotIndex,
        theirRoster.map(p => ({ playerName: p.playerName, position: p.position })));
      setTheirRosterKnown(true);
      setTheirRosterDirty(false);
      setEditingTheirRoster(false);
      setLeagueTeams(prev => prev.map(t => (t.slotIndex === selectedSlotIndex ? { ...t, rosterKnown: true } : t)));
    } catch {
      // leave the editor open with the unsaved chips
    } finally {
      setSavingRoster(false);
    }
  }

  async function handleRefreshRosters() {
    setRefreshingRosters(true);
    setRefreshMsg(null);
    try {
      const { updated } = await refreshOpponentRosters(leagueId);
      setRefreshMsg(`Updated ${updated} team roster${updated === 1 ? '' : 's'}.`);
      const teams = await getLeagueTeams(leagueId);
      setLeagueTeams(teams);
    } catch (err) {
      setRefreshMsg(err.response?.data?.error?.message || 'Refresh failed.');
    } finally {
      setRefreshingRosters(false);
    }
  }

  async function handlePopulateFromDraft() {
    setPopulatingRosters(true);
    setRefreshMsg(null);
    try {
      const { updated } = await populateRostersFromDraft(leagueId);
      setRefreshMsg(updated > 0
        ? `Filled in ${updated} opponent roster${updated === 1 ? '' : 's'} from the draft. Hand-edited rosters were left as-is.`
        : 'No opponent rosters to fill in from the draft yet.');
      const teams = await getLeagueTeams(leagueId);
      setLeagueTeams(teams);
    } catch (err) {
      setRefreshMsg(err.response?.data?.error?.message || 'Could not populate rosters from the draft.');
    } finally {
      setPopulatingRosters(false);
    }
  }

  function slotOptionLabel(t) {
    let base;
    if (t.opponent && t.opponent.displayName) {
      base = t.opponent.nickname
        ? `${t.opponent.displayName} (${t.opponent.nickname})`
        : t.opponent.displayName;
    } else if (t.teamName) {
      base = t.teamName;
    } else {
      base = `Team ${t.slotIndex + 1}`;
    }
    return `${base} · ${t.rosterKnown ? 'roster on file' : 'add their roster'}`;
  }

  function addReceivingPlayer(playerName) {
    setReceivingPlayers(prev => [...prev, playerName]);
    setReceiveSearch('');
  }

  function removeReceivingPlayer(playerName) {
    setReceivingPlayers(prev => prev.filter(n => n !== playerName));
  }

  // Team slots with embedded opponent info
  const [leagueTeams, setLeagueTeams] = useState([]);
  const [opponents, setOpponents] = useState([]);
  const [linkingSlot, setLinkingSlot] = useState(null); // slotIndex being updated

  const { week: currentWeek, seasonYear: resolvedSeasonYear } = getCurrentNflWeek();

  useEffect(() => {
    if (authLoading) return;
    if (!isLoggedIn) {
      navigate('/login?returnTo=/league/' + leagueId);
      return;
    }
    getLeague(leagueId)
      .then(data => {
        setLeague(data);
        setScoringRules(data.scoringRules || {});
        setRosterConfig(data.rosterConfig || {});
      })
      .catch(err => setError(err.response?.data?.error?.message || 'Failed to load league'))
      .finally(() => setLoading(false));
  }, [authLoading, isLoggedIn, leagueId, navigate]);

  useEffect(() => {
    if (!leagueId) return;
    getLeagueTeams(leagueId).then(setLeagueTeams).catch(() => {});
    getOpponents().then(setOpponents).catch(() => {});
    getAggregatedRankings().then(setRankings).catch(() => {});
  }, [leagueId]);

  useEffect(() => {
    if (!isPro) return;
    getAiUsage().then(data => setAiUsageRemaining(data.remaining)).catch(() => {});
  }, [isPro]);

  function startEditing() {
    setEditRoster(league.roster ? league.roster.map(p => ({ ...p })) : []);
    setScoringRules(league.scoringRules || {});
    setRosterConfig(league.rosterConfig || {});
    setSaveError(null);
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    setScoringRules(league.scoringRules || {});
    setRosterConfig(league.rosterConfig || {});
    setSaveError(null);
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteLeague(leagueId);
      navigate('/league');
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to delete league');
      setDeleting(false);
      setConfirmingDelete(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const [, , rosterConfigUpdated] = await Promise.all([
        updateLeagueRoster(leagueId, editRoster),
        updateLeagueScoringRules(leagueId, scoringRules),
        updateLeagueRosterConfig(leagueId, rosterConfig),
      ]);
      // Use the roster-config result — it's the last-fetched full DTO, so its scoringRules
      // (already re-derived by updateLeagueScoringRules) reflects both updates together.
      setLeague(rosterConfigUpdated);
      setScoringRules(rosterConfigUpdated.scoringRules || scoringRules);
      setRosterConfig(rosterConfigUpdated.rosterConfig || rosterConfig);
      setEditing(false);
    } catch (err) {
      setSaveError(err.response?.data?.error?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleStartSit() {
    setStartSit(null);
    setStartSitError(null);
    setStartSitLoading(true);
    try {
      const data = await getStartSitRecommendation(leagueId, currentWeek);
      setStartSit(data);
      setAiUsageRemaining(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('AI start/sit failed:', err);
      setStartSitError(getAiErrorMessage(err, 'AI start/sit failed. Please try again.'));
    } finally {
      setStartSitLoading(false);
    }
  }

  async function handleMatchupPreview(player, idx) {
    if (!MATCHUP_PREVIEW_ENABLED) return;
    if (activeMatchupIdx === idx) {
      setActiveMatchupIdx(null);
      return;
    }
    setActiveMatchupIdx(idx);
    if (matchupData[idx]) return; // already fetched
    setMatchupError(null);
    setMatchupData(prev => ({ ...prev, [idx]: 'loading' }));
    try {
      const data = await getMatchupPreview(player.playerName, currentWeek, Math.min(currentWeek + 3, 18), leagueId);
      setMatchupData(prev => ({ ...prev, [idx]: data }));
    } catch (err) {
      console.error('AI matchup preview failed:', err);
      setMatchupData(prev => ({ ...prev, [idx]: null }));
      setMatchupError(getAiErrorMessage(err, 'AI matchup preview failed. Please try again.'));
    }
  }

  async function handleLeagueOpponentLink(slotIndex, opponentId) {
    setLinkingSlot(slotIndex);
    try {
      const updated = await linkOpponentToLeagueSlot(leagueId, slotIndex, opponentId);
      setLeagueTeams(prev => prev.map(t => t.slotIndex === slotIndex ? updated : t));
    } catch {
      // silently ignore
    } finally {
      setLinkingSlot(null);
    }
  }

  async function handleLeagueOpponentUnlink(slotIndex) {
    setLinkingSlot(slotIndex);
    try {
      const updated = await unlinkOpponentFromLeagueSlot(leagueId, slotIndex);
      setLeagueTeams(prev => prev.map(t => t.slotIndex === slotIndex ? updated : t));
    } catch {
      // silently ignore
    } finally {
      setLinkingSlot(null);
    }
  }

  async function handleLeagueOpponentCreateAndLink(slotIndex, displayName) {
    setLinkingSlot(slotIndex);
    try {
      const created = await createOpponent({ displayName });
      setOpponents(prev => [...prev, created]);
      const updated = await linkOpponentToLeagueSlot(leagueId, slotIndex, created.id);
      setLeagueTeams(prev => prev.map(t => t.slotIndex === slotIndex ? updated : t));
    } catch {
      // silently ignore
    } finally {
      setLinkingSlot(null);
    }
  }

  async function handleAnalyzeTrade() {
    setTradeResult(null);
    setTradeError(null);
    setTradeLoading(true);
    try {
      const opts = {};
      if (selectedSlotIndex !== null) {
        opts.opponentSlotIndex = selectedSlotIndex;
        if (!theirRosterKnown || theirRosterDirty) {
          opts.theirRosterOverride = theirRoster.map(p => p.playerName);
        }
      } else if (isAdHocOpponent) {
        opts.theirRosterOverride = theirRoster.map(p => p.playerName);
      }
      const data = await analyzeTrade(leagueId, givingPlayers, receivingPlayers, opts);
      setTradeResult(data);
      setAiUsageRemaining(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('AI trade analysis failed:', err);
      setTradeError(getAiErrorMessage(err, 'Trade analysis failed. Please try again.'));
    } finally {
      setTradeLoading(false);
    }
  }

  if (authLoading || loading) {
    return (
      <div className='loading'>
        <div className='spinner' />
        <p>Loading league...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='card' style={{ maxWidth: 600, margin: '0 auto' }}>
        <div className='error-banner'>{error}</div>
        <button className='btn btn-secondary' onClick={() => navigate('/league')}>
          Back
        </button>
      </div>
    );
  }

  if (!league) return null;

  // A league belongs to a "past season" once the resolver has moved on to a
  // later season than the one the league was created for (e.g. a 2025 league
  // viewed after the 2025 window has closed and "now" resolves to 2026).
  const isPastSeasonLeague = league.seasonYear < resolvedSeasonYear;
  const startSitLockedToday = isMonday();
  const startSitDisabled = isPastSeasonLeague || startSitLockedToday;
  const startSitDisabledTooltip = isPastSeasonLeague
    ? 'Ask the Doctor for My Lineup isn\'t available for past seasons'
    : 'Start/Sit is locked on Mondays — check back Tuesday';

  const scoringLabel = {
    half_ppr: '0.5 PPR',
    ppr: 'PPR',
    standard: 'Standard',
    custom: 'Custom',
  }[league.scoringFormat] || league.scoringFormat;

  // Matchup Preview is a rolling window (not a playoff-only view), so it's visible every
  // in-season week regardless of which week it is. Gated on isPastSeasonLeague (same check
  // Start/Sit uses), not the blunt global isOffSeason flag -- a league for the upcoming season
  // should still show this during that season's own preseason gap (isOffSeason is true for
  // everyone between one season's window closing and the next one's kickoff, even though the
  // league itself isn't "past"); nfl_schedule already has next season's Week 1 by then (the
  // scraper fetches it regardless of isOffSeason), so there's real data to show.
  const matchupColumnVisible = MATCHUP_PREVIEW_ENABLED && !isPastSeasonLeague;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      {/* Header card */}
      <div className='card' style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <h2 style={{ margin: '0 0 4px' }}>{league.leagueName}</h2>
            <div className='draft-list-item-meta'>
              <span>{(league.sport || 'NFL').toUpperCase()}</span>
              <span>{league.seasonYear}</span>
              {isPastSeasonLeague && (
                <span className='draft-status-badge past' title="This league's season has ended">
                  Previous Season
                </span>
              )}
              <span className='draft-status-badge active'>{scoringLabel}</span>
              {league.seasonType === 'DYNASTY' && (
                <span className='draft-status-badge active' title='Dynasty/keeper league'>Dynasty</span>
              )}
              <span>{league.teamCount} teams</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!confirmingDelete ? (
              <button
                className='filter-pill'
                style={{ fontSize: '0.8rem' }}
                onClick={() => setConfirmingDelete(true)}
                disabled={editing}
              >
                Delete
              </button>
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>Delete league?</span>
                <button
                  className='filter-pill active'
                  style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? '…' : 'Yes'}
                </button>
                <button
                  className='filter-pill'
                  style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                  onClick={() => setConfirmingDelete(false)}
                  disabled={deleting}
                >
                  No
                </button>
              </span>
            )}
            <button className='btn btn-secondary' onClick={() => navigate('/league')}>
              Back
            </button>
          </div>
        </div>
      </div>

      {/* S1 — Start/Sit card */}
      <div className='card' style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: startSit ? 12 : 0 }}>
          <h3 style={{ margin: 0 }}>Week {currentWeek} Lineup</h3>
          <AiButton
            label='Ask the Doctor for My Lineup'
            isPro={isPro}
            usageRemaining={aiUsageRemaining}
            freeTooltip='Upgrade to Pro for AI lineup recommendations'
            onClick={handleStartSit}
            loading={startSitLoading}
            disabled={startSitDisabled}
            disabledTooltip={startSitDisabledTooltip}
          />
        </div>
        {startSitError && <p className='error-banner' style={{ marginTop: 8 }}>{startSitError}</p>}
        {startSit && (
          <StartSitPanel
            starts={startSit.starts}
            sits={startSit.sits}
            dataAsOf={startSit.dataAsOf}
            onClose={() => setStartSit(null)}
          />
        )}
      </div>

      {/* S3 — Trade Analyzer card */}
      <div className='card' style={{ marginBottom: 16 }}>
        <div className='trade-analyzer-section'>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <h4 style={{ margin: 0 }}>Trade Analyzer</h4>
            {(league?.yahooLeagueKey || league?.espnLeagueId) ? (
              <button
                className='filter-pill'
                onClick={handleRefreshRosters}
                disabled={refreshingRosters}
              >
                {refreshingRosters ? 'Refreshing…' : 'Refresh opponent rosters'}
              </button>
            ) : league?.sourceDraftId && (
              <button
                className='filter-pill'
                onClick={handlePopulateFromDraft}
                disabled={populatingRosters}
              >
                {populatingRosters ? 'Filling in…' : 'Populate opponent rosters from draft'}
              </button>
            )}
          </div>
          {refreshMsg && <p className='text-muted' style={{ fontSize: '0.8125rem', marginTop: 4 }}>{refreshMsg}</p>}

          <div style={{ margin: '10px 0' }}>
            <label className='trade-picker-label' htmlFor='trade-with-select'>Trading with</label>
            <select
              id='trade-with-select'
              className='search-input'
              style={{ width: '100%' }}
              value={tradeWith}
              disabled={!isPro}
              onChange={e => handleTradeWithChange(e.target.value)}
            >
              <option value=''>Rate on player value only</option>
              {leagueTeams.filter(t => !t.isOwnerSlot).map(t => (
                <option key={t.slotIndex} value={`slot:${t.slotIndex}`}>{slotOptionLabel(t)}</option>
              ))}
              <option value='other'>Someone else — enter their players</option>
            </select>
          </div>

          {showTheirRosterEditor && (
            <div className='trade-picker-column' style={{ marginBottom: 12 }}>
              <p className='trade-picker-label'>Their players</p>
              <input
                className='search-input'
                style={{ width: '100%', marginBottom: 8 }}
                placeholder="Search the other manager's players..."
                value={theirSearch}
                disabled={!isPro}
                onChange={e => setTheirSearch(e.target.value)}
              />
              {theirSearchResults.length > 0 && (
                <div className='trade-picker-list' style={{ marginBottom: 8 }}>
                  {theirSearchResults.map(r => (
                    <div
                      key={r.playerName}
                      className='trade-picker-item'
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <span>{r.playerName} <span className='trade-picker-pos'>{r.position}</span></span>
                      <button className='filter-pill' disabled={!isPro} onClick={() => addTheirPlayer(r.playerName, r.position)}>Add</button>
                    </div>
                  ))}
                </div>
              )}
              <div className='trade-picker-list'>
                {theirRoster.map(p => (
                  <div
                    key={p.playerName}
                    className='trade-picker-item'
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <span>{p.playerName} <span className='trade-picker-pos'>{p.position}</span></span>
                    <button className='filter-pill' disabled={!isPro} onClick={() => removeTheirPlayer(p.playerName)} title='Remove player'>×</button>
                  </div>
                ))}
              </div>
              {selectedSlotIndex !== null && (
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <button
                    className='filter-pill'
                    disabled={!isPro || savingRoster || theirRoster.length === 0}
                    onClick={handleSaveTheirRoster}
                  >
                    {savingRoster ? 'Saving…' : 'Save roster'}
                  </button>
                  {editingTheirRoster && (
                    <button className='filter-pill' disabled={savingRoster} onClick={cancelEditingTheirRoster}>
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {league?.roster && league.roster.length > 0 ? (
            <div className='trade-analyzer-pickers'>
              <div className='trade-picker-column'>
                <p className='trade-picker-label'>You give</p>
                <div className='trade-picker-list'>
                  {league.roster.map(p => (
                    <label key={p.playerName} className='trade-picker-item'>
                      <input
                        type='checkbox'
                        disabled={!isPro}
                        checked={givingPlayers.includes(p.playerName)}
                        onChange={e => {
                          if (e.target.checked) {
                            setGivingPlayers(prev => [...prev, p.playerName]);
                          } else {
                            setGivingPlayers(prev => prev.filter(n => n !== p.playerName));
                          }
                        }}
                      />
                      <span>{p.playerName} <span className='trade-picker-pos'>{p.position}</span></span>
                    </label>
                  ))}
                </div>
              </div>
              <div className='trade-picker-column'>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p className='trade-picker-label'>You receive</p>
                  {selectedSlotIndex !== null && theirRosterKnown && !editingTheirRoster && (
                    <button className='filter-pill' disabled={!isPro} onClick={startEditingTheirRoster}>
                      Edit their roster
                    </button>
                  )}
                </div>
                {selectedSlotIndex !== null && theirRosterKnown && !editingTheirRoster && theirRoster.length > 0 ? (
                  <div className='trade-picker-list'>
                    {theirRoster.map(p => (
                      <label key={p.playerName} className='trade-picker-item'>
                        <input
                          type='checkbox'
                          disabled={!isPro}
                          checked={receivingPlayers.includes(p.playerName)}
                          onChange={e => {
                            if (e.target.checked) {
                              addReceivingPlayer(p.playerName);
                            } else {
                              removeReceivingPlayer(p.playerName);
                            }
                          }}
                        />
                        <span>{p.playerName} <span className='trade-picker-pos'>{p.position}</span></span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <>
                    <input
                      className='search-input'
                      style={{ width: '100%', marginBottom: 8 }}
                      placeholder='Search players to receive...'
                      value={receiveSearch}
                      disabled={!isPro}
                      onChange={e => setReceiveSearch(e.target.value)}
                    />
                    {receiveSearchResults.length > 0 && (
                      <div className='trade-picker-list' style={{ marginBottom: 8 }}>
                        {receiveSearchResults.map(r => (
                          <div
                            key={r.playerName}
                            className='trade-picker-item'
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                          >
                            <span>{r.playerName} <span className='trade-picker-pos'>{r.position}</span></span>
                            <button
                              className='filter-pill'
                              disabled={!isPro}
                              onClick={() => addReceivingPlayer(r.playerName)}
                            >
                              Add
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className='trade-picker-list'>
                      {receivingPlayers.map(name => (
                        <div
                          key={name}
                          className='trade-picker-item'
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                          <span>{name}</span>
                          <button
                            className='filter-pill'
                            disabled={!isPro}
                            onClick={() => removeReceivingPlayer(name)}
                            title='Remove player'
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <p className='text-muted' style={{ fontSize: '0.875rem', marginBottom: 12 }}>
              Add players to your roster to use the Trade Analyzer.
            </p>
          )}
          <AiButton
            label='Ask the Doctor to Analyze'
            isPro={isPro}
            usageRemaining={isPro ? aiUsageRemaining : 0}
            freeTooltip='Upgrade to Pro to analyze trades.'
            onClick={handleAnalyzeTrade}
            loading={tradeLoading}
            disabled={givingPlayers.length === 0 || receivingPlayers.length === 0}
          />
          {tradeError && <p className='error-banner' style={{ marginTop: 8 }}>{tradeError}</p>}
          {tradeResult && (
            <TradeAnalysisPanel
              data={tradeResult}
              earlyAccess="Trade Analysis is in early access. Ratings are based on consensus rankings and roster composition. Recent performance data will be added in a future update."
              onClose={() => setTradeResult(null)}
            />
          )}
        </div>
      </div>

      {/* Roster card */}
      <div className='card' style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Roster ({editing ? editRoster.length : league.rosterCount} players)</h3>
          {!editing ? (
            <button className='btn btn-secondary' onClick={startEditing}>Edit</button>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className='btn btn-secondary' onClick={cancelEditing} disabled={saving}>Cancel</button>
              <button className='btn btn-primary' onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </div>

        {saveError && <div className='error-banner' style={{ marginBottom: 12 }}>{saveError}</div>}
        {matchupError && <div className='error-banner' style={{ marginBottom: 12 }}>{matchupError}</div>}

        {editing ? (
          <RosterBuilder roster={editRoster} onChange={setEditRoster} rankings={rankings} />
        ) : league.roster && league.roster.length > 0 ? (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Player', 'Pos', 'Team', 'How acquired', ...(matchupColumnVisible ? ['Matchups'] : [])].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {league.roster.map((p, i) => (
                  <>
                    <tr key={i} style={{ borderBottom: activeMatchupIdx === i ? 'none' : '1px solid var(--border)' }}>
                      <td style={{ padding: '6px 8px' }}>{p.playerName}</td>
                      <td style={{ padding: '6px 8px' }}>{p.position}</td>
                      <td style={{ padding: '6px 8px' }}>{p.nflTeam}</td>
                      <td style={{ padding: '6px 8px', color: 'var(--text-muted)' }}>{p.acquisitionType}</td>
                      {matchupColumnVisible && (
                        <td style={{ padding: '6px 8px' }}>
                          <MatchupPreviewBadge
                            outlook={matchupData[i]?.outlook || 'NEUTRAL'}
                            isPro={isPro}
                            onClick={() => handleMatchupPreview(p, i)}
                          />
                        </td>
                      )}
                    </tr>
                    {activeMatchupIdx === i && matchupData[i] && matchupData[i] !== 'loading' && (
                      <tr key={`matchup-${i}`} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td colSpan={matchupColumnVisible ? 5 : 4} style={{ padding: '0 8px 8px' }}>
                          <MatchupPreviewPanel
                            playerName={matchupData[i].playerName}
                            position={matchupData[i].position}
                            team={matchupData[i].team}
                            outlook={matchupData[i].outlook}
                            weekBreakdown={matchupData[i].weekBreakdown}
                            summary={matchupData[i].summary}
                            dataAsOf={matchupData[i].dataAsOf}
                            earlyAccess="Matchup Preview is in early access. Ratings use upcoming schedule difficulty and defensive rankings."
                            onClose={() => setActiveMatchupIdx(null)}
                          />
                        </td>
                      </tr>
                    )}
                    {activeMatchupIdx === i && matchupData[i] === 'loading' && (
                      <tr key={`matchup-loading-${i}`} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td colSpan={matchupColumnVisible ? 5 : 4} style={{ padding: '8px', textAlign: 'center', color: 'var(--text-muted)' }}>
                          <span className='ai-button-spinner' aria-label='Loading' style={{ marginRight: 8, verticalAlign: 'middle' }} />
                          Thinking…
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <p style={{ color: 'var(--text-muted)' }}>No players on roster.</p>
        )}
      </div>

      {/* Scoring Rules card — shares the same edit session as Roster/Roster Requirements
          (one combined save via handleSave), but gets its own Edit/Save/Cancel controls so it's
          discoverable without needing to know the Roster card's Edit button also unlocks it. */}
      <div className='card' style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Scoring Rules</h3>
          {!editing ? (
            <button className='btn btn-secondary' onClick={startEditing}>Edit</button>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className='btn btn-secondary' onClick={cancelEditing} disabled={saving}>Cancel</button>
              <button className='btn btn-primary' onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </div>
        <ScoringRulesEditor
          rules={scoringRules}
          onChange={setScoringRules}
          readOnly={!editing}
        />
      </div>

      {/* Roster Requirements card — same shared edit session as above. */}
      <div className='card' style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Roster Requirements</h3>
          {!editing ? (
            <button className='btn btn-secondary' onClick={startEditing}>Edit</button>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className='btn btn-secondary' onClick={cancelEditing} disabled={saving}>Cancel</button>
              <button className='btn btn-primary' onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </div>
        <RosterConfigEditor
          rosterConfig={rosterConfig}
          onChange={setRosterConfig}
          readOnly={!editing}
        />
      </div>

      {/* Teams card */}
      <div className='card' style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px' }}>Teams</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>Team</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>Manager</th>
            </tr>
          </thead>
          <tbody>
            {leagueTeams.map(team => (
              <tr key={team.slotIndex} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '6px 8px' }}>{team.teamName}</td>
                <td style={{ padding: '6px 8px' }}>
                  {team.isOwnerSlot ? (
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>You</span>
                  ) : (
                    <>
                      {team.opponent && (
                        // Explicit color instead of the browser-default link blue, which fails
                        // contrast against Cool mode's pure-black background (same class of bug
                        // as the earlier filter-pill/opponent-picker cool-mode fixes) — var(--primary)
                        // is theme-aware (still blue in light mode, neon green in Cool mode).
                        <Link
                          to={`/opponents/${team.opponent.id}`}
                          style={{ display: 'block', marginBottom: 4, color: 'var(--primary)' }}
                        >
                          {team.opponent.displayName}
                        </Link>
                      )}
                      <OpponentPicker
                        currentOpponent={team.opponent || null}
                        opponents={opponents}
                        loading={linkingSlot === team.slotIndex}
                        onLink={id => handleLeagueOpponentLink(team.slotIndex, id)}
                        onUnlink={() => handleLeagueOpponentUnlink(team.slotIndex)}
                        onCreateAndLink={name => handleLeagueOpponentCreateAndLink(team.slotIndex, name)}
                      />
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default LeagueView;
